import 'CssFolder/components/multi-select.scss';
import { Button, Icon, Icons, Intent, ListItem, SelectList } from "construct-ui";
import languageModel from "../../models/language.model";

/**
 * Single Select Component
 * 
 * Dropdown input component for single select. 
 * Supports key or object inputs.
 * 
 * @author Darrus
 */
export default function SingleSelectComponent() {
  const DEFAULT_ITEM_KEY = 'code';
  const DEFAULT_ITEM_VALUE = 'name';
  const DEFAULT_ALLOW_EMPTY = true;
  const DEFAULT_ONSELECT = (selectedItemOrCode) => { console.log('MultiSelectComponent: onSelect(selectedItem | selectedCode)') };
  const LIST_COMPONENT_X_PADDING = '2rem';

  // DO NOT MODIFY MANUALLY - Rendered width of this component
  let componentWidth;

  // COMMON FUNCTIONS
  const getItemList = (items, itemKey, itemValue, allowEmpty) => {
    let result = [];

    if (items && items.length > 0) {
      result = structuredClone(items);
    }

    if (allowEmpty) {
      result.unshift({
        [itemKey]: '',
        [itemValue]: languageModel.getText('LT00000201', 'Please select')
      });
    }

    return result;
  };

  const itemPredicate = (filterableByKey, itemKey, itemValue) => (query, item) => {
    const foundInValue = item[itemValue].trim().toLowerCase().includes(query.trim().toLowerCase());

    if (filterableByKey) {
      const foundInKey = item[itemKey].trim().toLowerCase().includes(query.trim().toLowerCase());
      return foundInKey || foundInValue;
    }

    return foundInValue;
  }

  // OBJECT FUNCTIONS
  const renderLabelObject = (items, selectedItem, itemKey, itemValue, allowEmpty) => {
    if (selectedItem && (selectedItem[itemKey] !== '' || !allowEmpty)) {
      return selectedItem[itemValue];
    }
    return '';
  };

  const renderItemObject = (selectedItem, itemKey, itemValue, itemIcons, allowEmpty, showKeyInLabel) => (item) => {
    return m(ListItem, {
      class: (allowEmpty && item[itemKey] === '') ? 'placeholder-text-color' : '',
      contentLeft: getIconObject(itemIcons, item, itemKey),
      label: m('span.text-wrap.text-break', showKeyInLabel && item[itemKey] ? item[itemValue] + ' - ' + item[itemKey] : item[itemValue]),
      selected: selectedItem && selectedItem[itemKey] !== '' && selectedItem[itemKey] === item[itemKey]
    });
  };

  const handleSelectObject = (itemKey, onSelect) => (item, e, index) => {
    onSelect(item, e, index);
  };

  const getIconObject = (itemIcons, item, itemKey) => {
    if (itemIcons) {
      return itemIcons[item[itemKey]];
    }
    return null;
  };

  // KEY FUNCTIONS
  const renderLabelKey = (items, selectedCode = '', itemKey, itemValue, allowEmpty) => {
    if (items && (selectedCode !== '' || !allowEmpty)) {
      const item = items.find((e) => e[itemKey] === selectedCode);
      if (item) {
        return item[itemValue];
      }
    }
    return '';
  };

  const renderItemKey = (selectedCode = '', itemKey, itemValue, itemIcons, allowEmpty, showKeyInLabel) => (item) => {
    return m(ListItem, {
      class: (allowEmpty && item[itemKey] === '') ? 'placeholder-text-color' : '',
      contentLeft: getIconObject(itemIcons, item, itemKey), // Cause we're getting from list item and not selected item
      label: m('span.text-wrap.text-break',
        showKeyInLabel && item[itemKey] ? item[itemValue] + ' - ' + item[itemKey] : item[itemValue]),
      selected: selectedCode !== '' && selectedCode === item[itemKey]
    });
  };

  const handleSelectKey = (itemKey, onSelect) => (item, e, index) => {
    onSelect(item[itemKey], e, index);
  };

  const getIconKey = (itemIcons, item, itemKey) => {
    if (itemIcons) {
      return itemIcons[item];
    }
    return null;
  };

  const renderLabelFunctions = [renderLabelObject, renderLabelKey];
  const handleSelectFunctions = [handleSelectObject, handleSelectKey];
  const renderItemFunctions = [renderItemObject, renderItemKey];
  const getIconFunctions = [getIconObject, getIconKey];

  // OTHER FUNCTIONS
  const getInputTrigger = (items, label, id, required, disabled, _class, icon, triggerAttrs) => {
    return m('.d-flex.justify-content-between.align-items-center.form-control.form-control-bg.select-list.cursor-pointer',
      icon,
      m('input.flex-fill.select-list.text-start.p-0.me-2.cursor-pointer', {
        autocomplete: 'off',
        id,
        type: 'text',
        placeholder: (items && items.length > 0)
          ? languageModel.getText('LT00000201', 'Please select')
          : languageModel.getText('LT00000363', 'No items available'),
        class: _class ? _class : '',
        oninput: (e) => {
          e.preventDefault();
          e.stopPropagation();
        },
        value: label,
        title: label,
        required,
        // Fix can input in select
        onkeydown: (e) => {
          e.preventDefault();
          e.stopPropagation();
        },
        onkeyup: (e) => {
          e.preventDefault();
          e.stopPropagation();
        },
        disabled,
        ...triggerAttrs,
        style: {
          width: `calc(0.45em * ${label?.length})`, // A hardcoded value found used to fit the content length
          border: 'none',
          background: 'transparent',
          ...triggerAttrs?.style,
        },
      }),
      m(Icon,
        {
          name: Icons.CHEVRON_DOWN,
          intent: Intent.PRIMARY
        }
      )
    );
  };

  const getButtonTrigger = (id, disabled, _class, buttonAttrs, triggerAttrs) => {
    return m(Button, {
      ...buttonAttrs,
      compact: typeof buttonAttrs?.compact === 'boolean' ? buttonAttrs.compact : true,
      class: 'select-list select-list-button '
        + (_class ? _class : buttonAttrs?.class ? buttonAttrs.class : ''),
      id: id ? id : buttonAttrs?.id,
      disabled,
      ...triggerAttrs,
    })
  };

  const updateComponentWidth = (parentWidth) => {
    if (!parentWidth) {
      componentWidth = undefined;
      return;
    }

    if (componentWidth != parentWidth) {
      componentWidth = parentWidth;
      m.redraw();
    }
  }

  const getListCSSStyle = (isButton) => {
    return {
      // make sure the dropdown list has the same width as the trigger component
      width: !isButton && componentWidth ? `calc(${componentWidth}px - ${LIST_COMPONENT_X_PADDING})` : undefined,
      maxWidth: isButton ? 'min(100%, 500px)' : undefined
    }
  }

  return {
    onupdate: ({ dom, attrs }) => {
      updateComponentWidth(dom.offsetWidth);
    },

    view: ({
      attrs: {
        activeIndex,
        allowEmpty = DEFAULT_ALLOW_EMPTY,
        buttonAttrs = null,
        class: _class,
        disabled = false,
        filterable = true,
        filterableByKey = false,
        showKeyInLabel = false,
        id = null,
        isButton = false,
        itemIcons = null,
        itemKey = DEFAULT_ITEM_KEY,
        items = [],
        itemValue = DEFAULT_ITEM_VALUE,
        keyOnly = false,
        onSelect = DEFAULT_ONSELECT,
        required = false,
        selectedItem: selectedItemOrCode = null,
        style = '',
        triggerAttrs,
        popoverAttrs,
      }
    }) => {
      return m(SelectList, {
        ...activeIndex != undefined && { activeIndex: activeIndex },
        filterable: items?.length > 0 ? filterable : false,
        closeOnSelect: true,
        items: !disabled && getItemList(items, itemKey, itemValue, allowEmpty),
        itemRender: renderItemFunctions[+keyOnly](selectedItemOrCode, itemKey, itemValue, itemIcons, allowEmpty, showKeyInLabel),
        itemPredicate: itemPredicate(filterableByKey, itemKey, itemValue),
        listAttrs: {
          style: getListCSSStyle(isButton)
        },
        onSelect: !disabled && handleSelectFunctions[+keyOnly](itemKey, onSelect),
        disabled,
        popoverAttrs: {
          hasArrow: false,
          ...popoverAttrs,
        },
        style,
        trigger: isButton
          ? getButtonTrigger(id, disabled, _class, buttonAttrs, triggerAttrs)
          : getInputTrigger(items, renderLabelFunctions[+keyOnly](items, selectedItemOrCode, itemKey, itemValue, allowEmpty), id, required, disabled, _class, getIconFunctions[+keyOnly](itemIcons, selectedItemOrCode, itemKey), triggerAttrs),
      })
    }
  }
}